<script lang="ts">
  /**
   * Detaljvy för en enskild rapport (S-08-04, omarbetad i S-04-16).
   *
   * Hard-rules:
   *  - Rapportör-identitet visas ALDRIG i denna vy. (UI-kontrakt vy 8 +
   *    A5/A9). "Visa rapportör"-flödet kräver backend-endpoint som inte
   *    är byggd i S-08-01 — kommer i framtida story.
   *  - walk_id visas som teknisk referens, ICKE-klickbar (admin-walk-vy
   *    = blocker / A13).
   *  - Inga fritextfält. Alla motiveringar kommer från fasta listor.
   *  - Status-uppdatering anropar PATCH med fast reason_code.
   *
   * IA: Vyn är en H3 sub-panel under ReportsView (som äger sidans H2).
   * Tidigare användes H2 vilket gav två peer-rubriker på samma sida —
   * förvirrande för skärmläsare.
   */
  import type { AdminReport } from './reportsClient';
  import { updateReportStatus } from './reportsClient';
  import {
    HANDLED_REASON_CODE,
    REJECT_REASONS,
    REOPEN_REASONS,
    adminStatusLabel,
    categoryLabel,
    type RejectReasonCode,
    type ReopenReasonCode,
  } from './reportLabels';
  import { formatDate } from '../util/format';

  type Props = {
    report: AdminReport;
    // eslint-disable-next-line no-unused-vars
    onUpdated: (updated: AdminReport) => void;
    onUnauthorized: () => void;
    onClose: () => void;
  };

  const { report, onUpdated, onUnauthorized, onClose }: Props = $props();

  type DialogKind = null | 'reject' | 'reopen';

  let dialog: DialogKind = $state(null);
  let rejectReason: RejectReasonCode | '' = $state('');
  let reopenReason: ReopenReasonCode | '' = $state('');
  let isSubmitting = $state(false);
  let errorMessage: string | null = $state(null);

  function openReject() {
    dialog = 'reject';
    rejectReason = '';
    errorMessage = null;
  }
  function openReopen() {
    dialog = 'reopen';
    reopenReason = '';
    errorMessage = null;
  }
  function closeDialog() {
    dialog = null;
    errorMessage = null;
  }

  async function markHandled(): Promise<void> {
    if (isSubmitting) return;
    isSubmitting = true;
    errorMessage = null;
    try {
      const result = await updateReportStatus(report.id, 'handled', HANDLED_REASON_CODE);
      handleResult(result);
    } finally {
      isSubmitting = false;
    }
  }

  async function submitReject(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (isSubmitting || rejectReason === '') return;
    isSubmitting = true;
    errorMessage = null;
    try {
      const result = await updateReportStatus(report.id, 'not_actionable', rejectReason);
      handleResult(result);
    } finally {
      isSubmitting = false;
    }
  }

  async function submitReopen(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (isSubmitting || reopenReason === '') return;
    isSubmitting = true;
    errorMessage = null;
    try {
      const result = await updateReportStatus(report.id, 'new', reopenReason);
      handleResult(result);
    } finally {
      isSubmitting = false;
    }
  }

  function handleResult(result: Awaited<ReturnType<typeof updateReportStatus>>): void {
    if (result.kind === 'ok') {
      dialog = null;
      onUpdated(result.report);
      return;
    }
    if (result.kind === 'unauthorized') {
      onUnauthorized();
      return;
    }
    if (result.kind === 'not_found') {
      errorMessage = 'Rapporten finns inte längre — listan kan vara inaktuell.';
      return;
    }
    errorMessage = result.message;
  }

  // Lat/lon visas med 4 decimaler — backend har redan avrundat till 4
  // decimaler innan lagring (CLAUDE.md geo-disciplin).
  function formatCoord(value: number): string {
    return value.toFixed(4);
  }
</script>

<article class="detail" aria-labelledby="detail-rubrik">
  <header>
    <h3 id="detail-rubrik">Rapportdetalj</h3>
    <button type="button" class="btn secondary" onclick={onClose} aria-label="Stäng rapportdetalj">
      Stäng
    </button>
  </header>

  <dl class="meta">
    <dt>Kategori</dt>
    <dd>{categoryLabel(report.category)}</dd>

    <dt>Position</dt>
    <dd>{formatCoord(report.latitude)}, {formatCoord(report.longitude)}</dd>

    <dt>Rapporterad</dt>
    <dd>{formatDate(report.reportedAt)}</dd>

    <dt>Status</dt>
    <dd>{adminStatusLabel(report.status)}</dd>

    <dt>Rapport-id</dt>
    <dd class="mono">{report.id}</dd>

    {#if report.walkId}
      <dt>Walk-referens</dt>
      <dd class="mono">
        <span>{report.walkId}</span>
        <small class="walk-ref-hint">Teknisk referens — kan inte öppnas härifrån.</small>
      </dd>
    {/if}

    {#if report.statusUpdatedAt}
      <dt>Status uppdaterad</dt>
      <dd>{formatDate(report.statusUpdatedAt)}</dd>
    {/if}
  </dl>

  {#if errorMessage}
    <p class="alert error" role="alert">{errorMessage}</p>
  {/if}

  <div class="actions">
    {#if report.status === 'new'}
      <button type="button" class="btn primary" onclick={markHandled} disabled={isSubmitting}>
        Markera behandlad
      </button>
      <button type="button" class="btn secondary" onclick={openReject} disabled={isSubmitting}>
        Avvisa
      </button>
    {:else}
      <button type="button" class="btn secondary" onclick={openReopen} disabled={isSubmitting}>
        Markera som ny igen
      </button>
    {/if}
  </div>

  {#if dialog === 'reject'}
    <form class="dialog" onsubmit={submitReject}>
      <h4>Avvisa rapport</h4>
      <p class="hint">Välj en motivering från listan.</p>
      <fieldset>
        <legend class="visually-hidden">Motivering</legend>
        {#each REJECT_REASONS as option (option.code)}
          <label class="radio">
            <input
              type="radio"
              name="reject-reason"
              value={option.code}
              bind:group={rejectReason}
              disabled={isSubmitting}
            />
            <span>{option.label}</span>
          </label>
        {/each}
      </fieldset>
      <div class="dialog-actions">
        <button type="button" class="btn secondary" onclick={closeDialog} disabled={isSubmitting}>
          Avbryt
        </button>
        <button type="submit" class="btn primary" disabled={isSubmitting || rejectReason === ''}>
          {isSubmitting ? 'Sparar…' : 'Bekräfta avvisning'}
        </button>
      </div>
    </form>
  {/if}

  {#if dialog === 'reopen'}
    <form class="dialog" onsubmit={submitReopen}>
      <h4>Markera som ny igen</h4>
      <p class="hint">Välj en motivering från listan.</p>
      <fieldset>
        <legend class="visually-hidden">Motivering</legend>
        {#each REOPEN_REASONS as option (option.code)}
          <label class="radio">
            <input
              type="radio"
              name="reopen-reason"
              value={option.code}
              bind:group={reopenReason}
              disabled={isSubmitting}
            />
            <span>{option.label}</span>
          </label>
        {/each}
      </fieldset>
      <div class="dialog-actions">
        <button type="button" class="btn secondary" onclick={closeDialog} disabled={isSubmitting}>
          Avbryt
        </button>
        <button type="submit" class="btn primary" disabled={isSubmitting || reopenReason === ''}>
          {isSubmitting ? 'Sparar…' : 'Bekräfta återöppning'}
        </button>
      </div>
    </form>
  {/if}

  <p class="footnote">
    Rapportör-identitet visas inte. Härden är byggt på principen att vi rapporterar platser, inte
    personer.
  </p>
</article>

<style>
  .detail {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 1.5rem;
    margin-top: 1rem;
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }
  h3 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    letter-spacing: -0.015em;
    color: var(--text);
  }
  h4 {
    margin: 0 0 0.5rem;
    font-size: 1.05rem;
    color: var(--text);
  }
  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }
  .btn.primary {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .btn.primary:hover:not(:disabled),
  .btn.primary:focus-visible:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  .btn.secondary:hover:not(:disabled),
  .btn.secondary:focus-visible:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  dl.meta {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.5rem 1rem;
    margin: 0 0 1.25rem;
    font-size: 0.95rem;
  }
  dl.meta dt {
    color: var(--text-dim);
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    padding-top: 0.15rem;
  }
  dl.meta dd {
    margin: 0;
    color: var(--text);
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .walk-ref-hint {
    display: block;
    margin-top: 0.2rem;
    font-family: inherit;
    font-size: 0.78rem;
    color: var(--text-dim);
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
  }
  .alert {
    padding: 0.6rem 0.85rem;
    border-radius: 4px;
    margin: 0 0 1rem;
    font-size: 0.9rem;
    border: 1px solid var(--rule);
  }
  .alert.error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: var(--danger);
  }
  .actions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
  .dialog {
    margin-top: 1.25rem;
    padding: 1.25rem;
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
  }
  .dialog .hint {
    margin: 0 0 1rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  fieldset {
    border: none;
    padding: 0;
    margin: 0 0 1rem;
    display: grid;
    gap: 0.4rem;
  }
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
  .radio {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    cursor: pointer;
    color: var(--text);
  }
  .dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
  }
  .footnote {
    margin: 1.25rem 0 0;
    font-size: 0.8rem;
    color: var(--text-dim);
  }
</style>
